import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { AlertCircle, Clock, Inbox } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STATUS, CAT_LABEL } from "@/lib/constants";
import { PageHead, Panel, Empty, sInput, sLabel } from "@/components/staff/Bits";
import Filters, { clean } from "@/components/staff/Filters";

const KPIS = [
  ["new", "New requests"], ["review", "Awaiting review"], ["assisting", "Being assisted"], ["followup", "Follow-up required"],
  ["referred", "Referred to agencies"], ["completed", "Completed / closed"], ["further_action", "Further action needed"],
];
const COLORS = ["#4A2E1A", "#3D6346", "#C59B27", "#B85028", "#7A6A5C", "#5E8A68", "#8C6A3F"];

function Kpi({ k, label, value }) {
  return (
    <Link to={`/office/cases?status=${k}`} data-testid={`kpi-${k}`} className="rounded-2xl bg-white border border-[#E7DFD5] p-5 hover:border-[#C59B27] transition-colors duration-150">
      <p className="text-sm text-[#605349] font-semibold">{label}</p>
      <p className="mt-2 text-3xl font-serif-head font-black text-[#4A2E1A]">{value}</p>
    </Link>
  );
}

function Alerts({ s }) {
  const items = [
    ["unattended", "Not yet attended (no human response)", s.unattended, Inbox, "/office/cases?status=new"],
    ["overdue", "Overdue follow-ups", s.overdue_followups, Clock, "/office/cases?overdue=1"],
    ["urgent", "Open urgent cases", s.urgent_open, AlertCircle, "/office/cases?priority=urgent"],
    ["unprioritized", "Open cases without a priority", s.unprioritized, AlertCircle, "/office/cases?priority=unset"],
  ];
  return (
    <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3 mb-6">
      {items.map(([k, label, v, Icon, to]) => (
        <Link key={k} to={to} data-testid={`alert-${k}`} className={`rounded-xl p-4 flex items-center gap-3 border ${v ? "bg-[#FBEADF] border-[#EDC3AA] text-[#8A3A1A]" : "bg-[#EBF2EC] border-[#C5D9C9] text-[#2D4A34]"}`}>
          <Icon className="w-5 h-5 shrink-0" /><span className="text-sm font-semibold flex-1">{label}</span><span className="text-xl font-black">{v}</span>
        </Link>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const { user, can } = useAuth();
  const [filters, setFilters] = useState({});
  const [s, setS] = useState(null);
  const [err, setErr] = useState(null);
  const [workers, setWorkers] = useState([]);
  const hasCases = can("cases:read_all") || can("cases:read_assigned");

  useEffect(() => { if (can("cases:manage")) api.get("/staff/assignable").then((r) => setWorkers(r.data)).catch(() => {}); }, [can]);
  useEffect(() => {
    if (!hasCases) return;
    api.get("/dashboard/stats", { params: clean(filters) }).then((r) => { setS(r.data); setErr(null); }).catch((e) => setErr(errMsg(e)));
  }, [filters, hasCases]);

  if (!hasCases) return (
    <div><PageHead title={`Welcome, ${user.name}`} sub="Your role does not include access to pastoral case records." />
      <Panel testid="no-case-access-panel"><p className="text-[15px]">{user.role === "sysadmin" ? "As System Administrator you manage users, the GKK directory and the audit log. Technical administration does not grant access to confidential case notes." : "Please see Home visits in the menu for visits assigned to you and confirm each one. Other limited assignments for GKK representatives and volunteers will come in a later phase."}</p></Panel>
    </div>);

  const workerFilter = can("cases:manage") && (
    <div><label className={sLabel} htmlFor="fw">Caseworker</label>
      <select id="fw" data-testid="filter-assigned" className={sInput} value={filters.assigned_to || ""} onChange={(e) => setFilters({ ...filters, assigned_to: e.target.value })}>
        <option value="">All</option><option value="unassigned">Unassigned</option>{workers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
      </select></div>);

  const statusData = s ? Object.entries(s.by_status).map(([k, v]) => ({ name: STATUS[k], count: v })) : [];
  const catData = s ? s.by_category.map((c) => ({ name: CAT_LABEL[c.category]?.split(" ")[0], count: c.count ?? 0, suppressed: c.suppressed })) : [];
  return (
    <div>
      <PageHead title="Pastoral dashboard" sub={can("cases:read_all") ? "Overview of requests you are authorized to see. Case descriptions are never shown here." : "Overview of cases assigned to you."} />
      <Filters value={filters} onChange={setFilters} extra={workerFilter} />
      {err && <p className="text-[#8A2E14] mb-4">{err}</p>}
      {!s ? <Empty>Loading…</Empty> : (<>
        <Alerts s={s} />
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3 mb-6">{KPIS.map(([k, l]) => <Kpi key={k} k={k} label={l} value={s.counts[k]} />)}</div>
        <div className="grid lg:grid-cols-2 gap-5">
          <Panel title="Cases by status" testid="chart-status">
            <div className="h-72"><ResponsiveContainer><BarChart data={statusData} layout="vertical" margin={{ left: 40 }}><XAxis type="number" allowDecimals={false} /><YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 12 }} /><Tooltip /><Bar dataKey="count" fill="#3D6346" radius={[0, 6, 6, 0]} /></BarChart></ResponsiveContainer></div>
          </Panel>
          <Panel title="Requests by category" testid="chart-category">
            {catData.length === 0 ? <Empty>No requests yet.</Empty> : (
              <div className="h-72"><ResponsiveContainer><BarChart data={catData}><XAxis dataKey="name" tick={{ fontSize: 12 }} /><YAxis allowDecimals={false} /><Tooltip cursor={{ fill: "#F4EBE4" }} formatter={(v, n, p) => (p.payload.suppressed ? "<5 (suppressed)" : v)} /><Bar dataKey="count" radius={[6, 6, 0, 0]}>{catData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Bar></BarChart></ResponsiveContainer></div>)}
            <p className="text-xs text-[#605349] mt-2">Statistical trends only. Groups smaller than 5 are hidden when filtering by GKK or category.</p>
          </Panel>
        </div>
      </>)}
    </div>
  );
}
